/**
 * V54: 좌석배치도 템플릿 Hooks
 *
 * 다중 템플릿 시스템을 위한 데이터 조회/뮤테이션 hooks
 *
 * @module shared/hooks/useSeatMapTemplates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  CompanyCode,
  SeatMapTemplate,
  SeatMapTemplateListItem,
  SeatMapObject,
  GetActiveSeatMapRequest,
  GetActiveSeatMapResponse,
  ListSeatMapTemplatesRequest,
  ListSeatMapTemplatesResponse,
  GetSeatMapTemplateRequest,
  GetSeatMapTemplateResponse,
  CreateSeatMapTemplateRequest,
  CreateSeatMapTemplateResponse,
  SaveSeatMapTemplateRequest,
  SaveSeatMapTemplateResponse,
  CopySeatMapTemplateRequest,
  CopySeatMapTemplateResponse,
  SetActiveSeatMapTemplateRequest,
  SetActiveSeatMapTemplateResponse,
  DeleteSeatMapTemplateRequest,
  DeleteSeatMapTemplateResponse,
} from '../types/seatMap';

// ============================================
// 디버그 로그 헬퍼
// ============================================

const isDev = process.env.NODE_ENV === 'development';

function log(fn: string, data: Record<string, unknown>) {
  // dev-only logging removed
}

// ============================================
// useActiveSeatMap - 운영 템플릿 조회
// ============================================

export interface UseActiveSeatMapOptions {
  companyCode: CompanyCode;
  enabled?: boolean;
}

export interface UseActiveSeatMapResult {
  template: SeatMapTemplate | null;
  activeTemplateId: string | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * V54: 운영 템플릿 조회 Hook
 *
 * 페이지 진입 시 항상 운영 템플릿을 표시하기 위해 사용
 */
export function useActiveSeatMap(options: UseActiveSeatMapOptions): UseActiveSeatMapResult {
  const { companyCode, enabled = true } = options;

  const [template, setTemplate] = useState<SeatMapTemplate | null>(null);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    const reqId = ++requestIdRef.current;
    log('getActiveSeatMap:start', { companyCode, reqId });

    setIsLoading(true);
    setError(null);

    try {
      if (!functions) {
        throw new Error('Firebase Functions not initialized');
      }

      const fn = httpsCallable<GetActiveSeatMapRequest, GetActiveSeatMapResponse>(
        functions,
        'getActiveSeatMap'
      );

      const result = await fn({ companyCode });

      if (!isMountedRef.current || requestIdRef.current !== reqId) {
        log('getActiveSeatMap:stale', { reqId, currentReqId: requestIdRef.current });
        return;
      }

      // V54.1: 상세 응답 로그
      log('getActiveSeatMap:response', {
        reqId,
        success: result.data.success,
        activeTemplateId: result.data.activeTemplateId,
        templateExists: !!result.data.template,
        templateName: result.data.template?.name ?? null,
        objectsLen: result.data.template?.objects?.length ?? 0,
        error: result.data.error ?? null,
      });

      if (result.data.success) {
        setTemplate(result.data.template);
        setActiveTemplateId(result.data.activeTemplateId);
      } else {
        throw new Error(result.data.error || '운영 템플릿 조회 실패');
      }
    } catch (err: any) {
      if (!isMountedRef.current || requestIdRef.current !== reqId) return;
      // V54.2: 에러 상세 로그 (code, message, details) - CORS 에러 감지
      const isCorsError = err.message?.includes('CORS') || err.message?.includes('Failed to fetch') || err.code === 'internal';
      log('getActiveSeatMap:error', {
        reqId,
        code: err.code || 'unknown',
        message: err.message || 'Unknown error',
        details: err.details || null,
        isCorsError,
        hint: isCorsError ? '함수가 배포되지 않았거나 IAM 설정 문제일 수 있습니다. firebase deploy --only functions 실행 필요.' : null,
      });
      setError(err);
    } finally {
      if (isMountedRef.current && requestIdRef.current === reqId) {
        setIsLoading(false);
      }
    }
  }, [companyCode, enabled]);

  useEffect(() => {
    isMountedRef.current = true;

    if (enabled) {
      setIsLoading(true);
      fetchData();
    } else {
      setIsLoading(false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData, enabled]);

  return { template, activeTemplateId, isLoading, error, refetch: fetchData };
}

// ============================================
// useSeatMapTemplateList - 템플릿 목록 조회
// ============================================

export interface UseSeatMapTemplateListOptions {
  companyCode: CompanyCode;
  enabled?: boolean;
}

export interface UseSeatMapTemplateListResult {
  templates: SeatMapTemplateListItem[];
  activeTemplateId: string | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * V54: 템플릿 목록 조회 Hook
 *
 * 편집권한자가 템플릿 선택 모달에서 목록을 보기 위해 사용
 */
export function useSeatMapTemplateList(
  options: UseSeatMapTemplateListOptions
): UseSeatMapTemplateListResult {
  const { companyCode, enabled = true } = options;

  const [templates, setTemplates] = useState<SeatMapTemplateListItem[]>([]);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    const reqId = ++requestIdRef.current;
    log('listSeatMapTemplates:start', { companyCode, reqId });

    setIsLoading(true);
    setError(null);

    try {
      if (!functions) {
        throw new Error('Firebase Functions not initialized');
      }

      const fn = httpsCallable<ListSeatMapTemplatesRequest, ListSeatMapTemplatesResponse>(
        functions,
        'listSeatMapTemplates'
      );

      const result = await fn({ companyCode });

      if (!isMountedRef.current || requestIdRef.current !== reqId) return;

      if (result.data.success) {
        setTemplates(result.data.templates);
        setActiveTemplateId(result.data.activeTemplateId);
        log('listSeatMapTemplates:success', {
          reqId,
          count: result.data.templates.length,
          activeTemplateId: result.data.activeTemplateId,
        });
      } else {
        throw new Error(result.data.error || '템플릿 목록 조회 실패');
      }
    } catch (err: any) {
      if (!isMountedRef.current || requestIdRef.current !== reqId) return;
      // V54.2: 에러 상세 로그 (code, message, details) - CORS 에러 감지
      const isCorsError = err.message?.includes('CORS') || err.message?.includes('Failed to fetch') || err.code === 'internal';
      log('listSeatMapTemplates:error', {
        reqId,
        code: err.code || 'unknown',
        message: err.message || 'Unknown error',
        details: err.details || null,
        isCorsError,
        hint: isCorsError ? '함수가 배포되지 않았거나 IAM 설정 문제일 수 있습니다. firebase deploy --only functions 실행 필요.' : null,
      });
      setError(err);
    } finally {
      if (isMountedRef.current && requestIdRef.current === reqId) {
        setIsLoading(false);
      }
    }
  }, [companyCode, enabled]);

  useEffect(() => {
    isMountedRef.current = true;

    if (enabled) {
      setIsLoading(true);
      fetchData();
    } else {
      setIsLoading(false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData, enabled]);

  return { templates, activeTemplateId, isLoading, error, refetch: fetchData };
}

// ============================================
// useSeatMapTemplate - 단일 템플릿 조회
// ============================================

export interface UseSeatMapTemplateOptions {
  companyCode: CompanyCode;
  templateId: string | null;
  enabled?: boolean;
}

export interface UseSeatMapTemplateResult {
  template: SeatMapTemplate | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * V54: 단일 템플릿 조회 Hook
 *
 * 편집 모드에서 선택한 템플릿을 로드하기 위해 사용
 */
export function useSeatMapTemplate(
  options: UseSeatMapTemplateOptions
): UseSeatMapTemplateResult {
  const { companyCode, templateId, enabled = true } = options;

  const [template, setTemplate] = useState<SeatMapTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(enabled && !!templateId);
  const [error, setError] = useState<Error | null>(null);

  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!enabled || !templateId) return;

    const reqId = ++requestIdRef.current;
    log('getSeatMapTemplate:start', { companyCode, templateId, reqId });

    setIsLoading(true);
    setError(null);

    try {
      if (!functions) {
        throw new Error('Firebase Functions not initialized');
      }

      const fn = httpsCallable<GetSeatMapTemplateRequest, GetSeatMapTemplateResponse>(
        functions,
        'getSeatMapTemplate'
      );

      const result = await fn({ companyCode, templateId });

      if (!isMountedRef.current || requestIdRef.current !== reqId) return;

      if (result.data.success) {
        setTemplate(result.data.template);
        log('getSeatMapTemplate:success', {
          reqId,
          hasTemplate: !!result.data.template,
          name: result.data.template?.name,
        });
      } else {
        throw new Error(result.data.error || '템플릿 조회 실패');
      }
    } catch (err: any) {
      if (!isMountedRef.current || requestIdRef.current !== reqId) return;
      // V54.2: 에러 상세 로그 (code, message, details) - CORS 에러 감지
      const isCorsError = err.message?.includes('CORS') || err.message?.includes('Failed to fetch') || err.code === 'internal';
      log('getSeatMapTemplate:error', {
        reqId,
        code: err.code || 'unknown',
        message: err.message || 'Unknown error',
        details: err.details || null,
        isCorsError,
        hint: isCorsError ? '함수가 배포되지 않았거나 IAM 설정 문제일 수 있습니다. firebase deploy --only functions 실행 필요.' : null,
      });
      setError(err);
    } finally {
      if (isMountedRef.current && requestIdRef.current === reqId) {
        setIsLoading(false);
      }
    }
  }, [companyCode, templateId, enabled]);

  useEffect(() => {
    isMountedRef.current = true;

    if (enabled && templateId) {
      setIsLoading(true);
      fetchData();
    } else {
      setIsLoading(false);
      setTemplate(null);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData, enabled, templateId]);

  return { template, isLoading, error, refetch: fetchData };
}

// ============================================
// useSeatMapTemplateMutations - 뮤테이션 Hooks
// ============================================

export interface UseSeatMapTemplateMutationsResult {
  /** 새 템플릿 생성 */
  createTemplate: (params: {
    name?: string;
    copyFromTemplateId?: string;
  }) => Promise<{ templateId: string; name: string }>;

  /** 템플릿 저장 */
  saveTemplate: (params: {
    templateId: string;
    name: string;
    objects: SeatMapObject[];
    canvasWidth?: number;
    canvasHeight?: number;
    gridSize?: number;
  }) => Promise<void>;

  /** 템플릿 간 복사 */
  copyTemplate: (params: {
    fromTemplateId: string;
    toTemplateId: string;
  }) => Promise<void>;

  /** 운영 템플릿 지정 */
  setActiveTemplate: (templateId: string) => Promise<void>;

  /** 템플릿 삭제 */
  deleteTemplate: (templateId: string) => Promise<void>;

  /** 뮤테이션 진행 중 */
  isProcessing: boolean;
}

/**
 * V54: 템플릿 뮤테이션 Hook
 */
export function useSeatMapTemplateMutations(
  companyCode: CompanyCode
): UseSeatMapTemplateMutationsResult {
  const [isProcessing, setIsProcessing] = useState(false);

  const createTemplate = useCallback(
    async (params: { name?: string; copyFromTemplateId?: string }) => {
      setIsProcessing(true);
      try {
        if (!functions) throw new Error('Firebase Functions not initialized');

        const fn = httpsCallable<CreateSeatMapTemplateRequest, CreateSeatMapTemplateResponse>(
          functions,
          'createSeatMapTemplate'
        );

        const result = await fn({
          companyCode,
          name: params.name,
          copyFromTemplateId: params.copyFromTemplateId,
        });

        if (!result.data.success) {
          throw new Error(result.data.error || '템플릿 생성 실패');
        }

        log('createTemplate:success', { templateId: result.data.templateId, name: result.data.name });
        return { templateId: result.data.templateId, name: result.data.name };
      } finally {
        setIsProcessing(false);
      }
    },
    [companyCode]
  );

  const saveTemplate = useCallback(
    async (params: {
      templateId: string;
      name: string;
      objects: SeatMapObject[];
      canvasWidth?: number;
      canvasHeight?: number;
      gridSize?: number;
    }) => {
      setIsProcessing(true);
      try {
        if (!functions) throw new Error('Firebase Functions not initialized');

        const fn = httpsCallable<SaveSeatMapTemplateRequest, SaveSeatMapTemplateResponse>(
          functions,
          'saveSeatMapTemplate'
        );

        const result = await fn({
          companyCode,
          templateId: params.templateId,
          name: params.name,
          objects: params.objects,
          canvasWidth: params.canvasWidth,
          canvasHeight: params.canvasHeight,
          gridSize: params.gridSize,
        });

        if (!result.data.success) {
          throw new Error(result.data.error || '템플릿 저장 실패');
        }

        log('saveTemplate:success', { templateId: params.templateId, name: params.name });
      } finally {
        setIsProcessing(false);
      }
    },
    [companyCode]
  );

  const copyTemplate = useCallback(
    async (params: { fromTemplateId: string; toTemplateId: string }) => {
      setIsProcessing(true);
      try {
        if (!functions) throw new Error('Firebase Functions not initialized');

        const fn = httpsCallable<CopySeatMapTemplateRequest, CopySeatMapTemplateResponse>(
          functions,
          'copySeatMapTemplate'
        );

        const result = await fn({
          companyCode,
          fromTemplateId: params.fromTemplateId,
          toTemplateId: params.toTemplateId,
        });

        if (!result.data.success) {
          throw new Error(result.data.error || '템플릿 복사 실패');
        }

        log('copyTemplate:success', { from: params.fromTemplateId, to: params.toTemplateId });
      } finally {
        setIsProcessing(false);
      }
    },
    [companyCode]
  );

  const setActiveTemplate = useCallback(
    async (templateId: string) => {
      setIsProcessing(true);
      try {
        if (!functions) throw new Error('Firebase Functions not initialized');

        const fn = httpsCallable<SetActiveSeatMapTemplateRequest, SetActiveSeatMapTemplateResponse>(
          functions,
          'setActiveSeatMapTemplate'
        );

        const result = await fn({ companyCode, templateId });

        if (!result.data.success) {
          throw new Error(result.data.error || '운영 템플릿 지정 실패');
        }

        log('setActiveTemplate:success', { templateId });
      } finally {
        setIsProcessing(false);
      }
    },
    [companyCode]
  );

  const deleteTemplate = useCallback(
    async (templateId: string) => {
      setIsProcessing(true);
      try {
        if (!functions) throw new Error('Firebase Functions not initialized');

        const fn = httpsCallable<DeleteSeatMapTemplateRequest, DeleteSeatMapTemplateResponse>(
          functions,
          'deleteSeatMapTemplate'
        );

        const result = await fn({ companyCode, templateId });

        if (!result.data.success) {
          throw new Error(result.data.error || '템플릿 삭제 실패');
        }

        log('deleteTemplate:success', { templateId });
      } finally {
        setIsProcessing(false);
      }
    },
    [companyCode]
  );

  return {
    createTemplate,
    saveTemplate,
    copyTemplate,
    setActiveTemplate,
    deleteTemplate,
    isProcessing,
  };
}
